import { ThinXIcon } from 'src/shared/components/icons/toasts/thin-x-icon'
import { Button } from 'src/shared/components/ui/buttons/button'
import DashboardModal from 'src/shared/components/ui/dashboard/dashboard-modal'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesEnterpriseOnlyCurrent } from 'src/shared/utils/tailwind-styles'

import type { EnterpriseBusinessImportContinueResponse } from '../api/use-enterprise-business-import-continue'

export interface FailedUploadModalProps {
  isOpen: boolean
  setOpen: (open: boolean) => void
  data: EnterpriseBusinessImportContinueResponse
  onRetry: () => void
  onClose: () => void
}

export default function FailedUploadModal({ isOpen, setOpen, data, onRetry, onClose }: FailedUploadModalProps) {
  const { isMobile } = useBreakpoint()

  const failedCount = data.summary.failed

  const rows = data.rows.filter((row) => row.status === 'failed')

  return (
    <DashboardModal onClose={() => {}} open={isOpen}>
      <div className="relative flex h-[650px] w-[calc(100vw-20px)] max-w-[550px] flex-col overflow-hidden rounded-[25px] bg-white">
        {/* Close button */}
        <div
          onClick={() => setOpen(false)}
          className={cn(
            'absolute right-[20px] top-[28px] z-10 flex size-[40px] shrink-0 items-center justify-center rounded-full bg-accent hover:cursor-pointer',
          )}
        >
          <ThinXIcon size={isMobile ? 14 : 13} className="text-primary" />
        </div>

        <div className="flex flex-1 flex-col overflow-y-auto px-5 pt-10 xs:px-[45px] xs:pb-0 xs:pt-[39px]">
          <div className="mx-auto flex min-h-0 w-full max-w-[460px] flex-1 flex-col">
            {/* Header */}
            <h2 className="text-left">
              <p className="text-[32px] font-semibold leading-tight tracking-[-0.32px] text-primary">
                {`${failedCount} record${failedCount === 1 ? '' : 's'} failed`}
              </p>
              <p className="mt-2 text-[16px] font-medium leading-[1.6] text-muted">
                {'Make sure to update and reupload failed records'}
              </p>
            </h2>

            {/* Content */}
            <div
              className={cn(
                'mt-5 min-h-0 flex-1 overflow-y-auto pb-8 pr-1',
                ScrollBarTailwindClassesEnterpriseOnlyCurrent,
              )}
            >
              <div className="flex min-h-[80px] flex-wrap content-start gap-[6px]">
                {rows.map((row) => (
                  <div
                    key={`row-${row.rowNumber}`}
                    className="flex min-h-[36px] w-full flex-col justify-center gap-1 rounded-[15px] bg-accent p-5 py-4"
                  >
                    <span className="text-base font-semibold leading-[1.35] tracking-[-0.16px] text-primary">{`Row #${row.rowNumber}`}</span>
                    <span className="text-[14px] font-medium text-muted">{row.error}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Invite button */}
        <div className={cn('flex h-20 items-center border-t border-lightBorder px-5 py-[15px] xs:px-5')}>
          <div className="mx-auto flex w-full max-w-[510px] items-center justify-between gap-4">
            <Button
              variant="link"
              className="h-[50px] w-fit px-0 text-[15px] font-semibold text-primary"
              onClick={onRetry}
            >
              Re-upload
            </Button>
            <Button
              variant="default"
              className="h-[50px] w-fit rounded-full bg-primary p-3 px-10 text-[15px] font-semibold text-white"
              onClick={onClose}
            >
              Continue regardless
            </Button>
          </div>
        </div>
      </div>
    </DashboardModal>
  )
}
